import { CellGroup, CellItem } from "@/components/cell";
import { RightIcon } from "@/components/icons/right-icon";
import { StackedModal } from "@/components/modal/stacked-modal";
import IconButton from "@/components/ui/buttons/IconButton";
import { FavouriteFoodsSheet } from "@/features/ai-chat/components/favourite-foods-sheet";
import { ShopListModal } from "@/features/shop-list/components/shop-list-modal";
import AppSettingsPage from "@/features/user-profile/pages/app-settings";
import DietaryRequirementPage from "@/features/user-profile/pages/dietary-requirement";
import FavoriteCuisinesPage from "@/features/user-profile/pages/favorite-cuisines";
import FoodAvoidsPage from "@/features/user-profile/pages/food-avoids";
import PersonalInfoPage from "@/features/user-profile/pages/personal-info";
import PreferencesPage from "@/features/user-profile/pages/preferences";
import SubscriptionManagementPage from "@/features/user-profile/pages/subscription-management";
import SubscriptionUpgradePage from "@/features/user-profile/pages/subscription-upgrade";
import SupermarketsPage from "@/features/user-profile/pages/supermarkets";
import { useIsSubscriptionEnabled } from "@/hooks/use-is-subscription-enabled";
import { textVariants } from "@/utils/text-variants";
import { memo, useState } from "react";
import { MemoryRouter, Redirect, Route, Switch, useHistory, useLocation } from "react-router-dom";
import { LogoutButton } from "./logout-button";
import { SettingDescription } from "./setting-description";

interface ProfileSettingsModalProps {
  /** Unused since StackedModal stopped card-presenting; kept optional for callers that still pass a page ref. */
  pageRef?: React.RefObject<HTMLElement>;
  isOpen: boolean;
  onDismiss: () => void;
  /** Starting route inside the modal's MemoryRouter (e.g. "/profile"). Defaults to "/". */
  initialPath?: string;
}

interface SettingsListProps {
  onYourFoodsClick: () => void;
  onShoppingListClick: () => void;
  onProfileClick: () => void;
  onPreferencesClick: () => void;
  onAppSettingsClick: () => void;
  onSubscriptionClick: () => void;
  showSubscriptionSection: boolean;
  onDismiss: () => void;
}

interface ProfileSettingsModalContentProps extends ProfileSettingsModalProps {
  onYourFoodsClick: () => void;
  onShoppingListClick: () => void;
}

const SettingsList = memo(function SettingsList({
  onYourFoodsClick,
  onShoppingListClick,
  onProfileClick,
  onPreferencesClick,
  onAppSettingsClick,
  onSubscriptionClick,
  showSubscriptionSection,
  onDismiss,
}: SettingsListProps) {
  return (
    <div className="safe-area-bottom px-9">
      <div className="flex items-center justify-between gap-11 pb-13">
        <div className="space-y-2.5">
          <h1 className={textVariants.largeTitle}>Settings</h1>
          <p className={textVariants.bodySingleLine}>Let's personalize some things for you</p>
        </div>
      </div>
      <div className="flex flex-col gap-2 py-2">
        {/* <CellGroup>
          <CellItem label="Your foods" onClick={onYourFoodsClick} />
        </CellGroup>

        <SettingDescription description="View and manage your saved meals." /> */}

        <CellGroup>
          <CellItem label="Your profile" href="/settings/profile" onClick={onProfileClick} />
        </CellGroup>

        <SettingDescription description="Let's personalize some things for you" />

        <CellGroup>
          <CellItem label="App settings" href="/settings" onClick={onAppSettingsClick} />
        </CellGroup>

        <SettingDescription description="Edit email, password and other app preferences." />

        <CellGroup>
          <CellItem label="Your preferences" href="/preferences" onClick={onPreferencesClick} />
        </CellGroup>

        <SettingDescription description="Edit supermarkets, disliked foods, cuisines, dietary requirements etc." />
{/* 
        {showSubscriptionSection ? (
          <>
            <CellGroup>
              <CellItem
                label="Your subscriptions"
                href="/subscriptions"
                onClick={onSubscriptionClick}
              />
            </CellGroup>
            <SettingDescription description="Edit your subscriptions - upgrade, downgrade or cancel your current plan." />
          </>
        ) : null} */}
        <LogoutButton onLogoutComplete={onDismiss} className="mt-10" />
      </div>
    </div>
  );
});

const ProfileSettingsModalContent = memo(function ProfileSettingsModalContent({
  pageRef,
  isOpen,
  onDismiss,
  onYourFoodsClick,
  onShoppingListClick,
}: ProfileSettingsModalContentProps) {
  const history = useHistory();
  const location = useLocation();
  const { isEnabled: showSubscriptionSection } = useIsSubscriptionEnabled();
  const isRootRoute = location.pathname === "/" || location.pathname === "";

  return (
    <StackedModal
      pageRef={pageRef}
      isOpen={isOpen}
      onDidDismiss={onDismiss}
      title={isRootRoute ? "" : undefined}
      showHeader={isRootRoute}
      showConfirmButton={false}
      renderCloseButton={(onClose) => (
        <IconButton onClick={onClose}>
          <RightIcon className="h-11 w-11 rotate-90 p-2" />
        </IconButton>
      )}
      forceOverscroll={false}
    >
      <Switch>
        <Route
          exact
          path="/"
          render={() => (
            <SettingsList
              onYourFoodsClick={onYourFoodsClick}
              onShoppingListClick={onShoppingListClick}
              onProfileClick={() => history.push("/profile")}
              onPreferencesClick={() => history.push("/preferences")}
              onAppSettingsClick={() => history.push("/settings")}
              onSubscriptionClick={() => history.push("/subscriptions")}
              showSubscriptionSection={showSubscriptionSection}
              onDismiss={onDismiss}
            />
          )}
        />
        <Route
          path="/profile"
          render={() => (
            <PersonalInfoPage
              onBack={() => (history.length <= 1 ? onDismiss() : history.goBack())}
            />
          )}
        />
        <Route
          path="/settings"
          render={() => (
            <AppSettingsPage
              onBack={() => (history.length <= 1 ? onDismiss() : history.goBack())}
            />
          )}
        />
        <Route
          path="/preferences"
          render={() => (
            <PreferencesPage
              onBack={() => (history.length <= 1 ? onDismiss() : history.goBack())}
            />
          )}
        />
        <Route
          exact
          path="/subscriptions"
          render={() =>
            showSubscriptionSection ? (
              <SubscriptionManagementPage onBack={() => history.goBack()} />
            ) : (
              <Redirect to="/" />
            )
          }
        />
        <Route
          path="/subscriptions/upgrade"
          render={() =>
            showSubscriptionSection ? (
              <SubscriptionUpgradePage onBack={() => history.goBack()} />
            ) : (
              <Redirect to="/" />
            )
          }
        />
        <Route
          path="/food-avoids"
          render={() => <FoodAvoidsPage onBack={() => history.goBack()} />}
        />
        <Route
          path="/supermarkets"
          render={() => <SupermarketsPage onBack={() => history.goBack()} />}
        />
        <Route
          path="/cuisines"
          render={() => <FavoriteCuisinesPage onBack={() => history.goBack()} />}
        />
        <Route
          path="/dietary-requirement"
          render={() => <DietaryRequirementPage onBack={() => history.goBack()} />}
        />
      </Switch>
    </StackedModal>
  );
});

export const ProfileSettingsModal = memo(function ProfileSettingsModal({
  pageRef,
  isOpen,
  onDismiss,
  initialPath = "/",
}: ProfileSettingsModalProps) {
  const [isYourFoodsOpen, setIsYourFoodsOpen] = useState(false);
  const [isShoppingListOpen, setIsShoppingListOpen] = useState(false);

  return (
    <>
      <MemoryRouter initialEntries={[initialPath]} key={isOpen ? `open-${initialPath}` : "closed"}>
        <ProfileSettingsModalContent
          pageRef={pageRef}
          isOpen={isOpen}
          onDismiss={onDismiss}
          onYourFoodsClick={() => setIsYourFoodsOpen(true)}
          onShoppingListClick={() => setIsShoppingListOpen(true)}
        />
      </MemoryRouter>

      <FavouriteFoodsSheet isOpen={isYourFoodsOpen} onDismiss={() => setIsYourFoodsOpen(false)} />

      <ShopListModal
        isOpen={isShoppingListOpen}
        onDismiss={() => setIsShoppingListOpen(false)}
        pageRef={pageRef ?? { current: null }}
      />
    </>
  );
});
